import { mergeAttributes, Node, nodeInputRule } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import ResizableImageComponent from './ResizableImageComponent';

// Regular expression for matching image markdown syntax
const IMAGE_INPUT_REGEX = /!\[(.+|:?)]\((\S+)(?:(?:\s+)["'](\S+)["'])?\)/;

// Helper function for determining alignment from style string
function getAlignmentFromStyle(style: string): 'left' | 'center' | 'right' {  
  if (style.includes('margin: 0 auto')) return 'center';
  if (style.includes('margin: 0 auto 0 0')) return 'left';
  if (style.includes('margin: 0 0 0 auto')) return 'right';
  
  return 'center';
}

export interface ImageOptions {
  inline: boolean;
  allowBase64: boolean;
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    resizableImage: {
      /**
       * Add an image
       */
      setResizableImage: (options: { src: string; alt?: string; title?: string; width?: string; height?: string; alignment?: 'left' | 'center' | 'right' }) => ReturnType;
    };
  }
}

export const ResizableImage = Node.create<ImageOptions>({
  name: 'resizableImage',

  addOptions() {
    return {
      inline: false,
      allowBase64: false,
      HTMLAttributes: {},
    };
  },

  group() {
    return this.options.inline ? 'inline' : 'block';
  },

  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null,
      },
      alt: {
        default: null,
      },
      title: {
        default: null,
      },
      width: {
        default: '100%',
      },
      height: {
        default: 'auto',
      },
      alignment: {
        default: 'center',
        parseHTML: element => element.getAttribute('data-alignment') || 'center',
        renderHTML: attributes => {
          return {
            'data-alignment': attributes.alignment,
            style: `display: block; margin: ${attributes.alignment === 'center' ? '0 auto' : attributes.alignment === 'left' ? '0 auto 0 0' : '0 0 0 auto'};`,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'img[src]',
        getAttrs: (node) => {
          if (typeof node === 'string') return {};
          
          const element = node as HTMLElement;
          const width = element.getAttribute('width') || element.style.width;
          const height = element.getAttribute('height') || element.style.height;
          const alignment = element.getAttribute('data-alignment') || getAlignmentFromStyle(element.style.cssText);

          return {
            src: element.getAttribute('src'),
            alt: element.getAttribute('alt'),
            title: element.getAttribute('title'),
            width: width || '100%',
            height: height || 'auto',
            alignment: alignment || 'center',
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const { alignment, ...otherAttributes } = HTMLAttributes;
    
    return [
      'img',
      mergeAttributes(
        this.options.HTMLAttributes,
        otherAttributes,
        {
          'data-alignment': alignment,
          style: `display: block; margin: ${alignment === 'center' ? '0 auto' : alignment === 'left' ? '0 auto 0 0' : '0 0 0 auto'};`,
        }
      ),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageComponent);
  },

  addCommands() {
    return {
      setResizableImage:
        (options) => ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
    };
  },

  addInputRules() {
    return [
      nodeInputRule({
        find: IMAGE_INPUT_REGEX,
        type: this.type,
        getAttributes: (match) => {
          const [, alt, src, title] = match;
          return { src, alt, title };
        },
      }),
    ];
  },
});

export default ResizableImage; 